(() => {
  'use strict';
  if (window.daceinCarouselRegistered) return;
  window.daceinCarouselRegistered = true;
  const instances = new WeakMap();
  function init(root) {
    if (instances.has(root)) return;
    const viewport = root.querySelector('[data-dch-viewport]');
    const panels = [...root.querySelectorAll('[data-dch-panel]')];
    const dots = [...root.querySelectorAll('[data-dch-dot]')];
    const status = root.querySelector('[data-dch-status]');
    const controller = new AbortController();
    const signal = controller.signal;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let current = 0;
    let target = null;
    let settleTimer;
    let measureFrame;
    let keepChangedSlideVisible = false;
    let mouseDrag = null;
    let suppressClick = false;
    const autoplayDelay = 5000;
    const contactPanel = document.getElementById('DaceinContact');
    let autoplayTimer;
    let isInView = false;
    let hovered = false;
    let pointerHeld = false;
    let width = viewport.clientWidth;
    function scheduleAutoplay() {
      clearTimeout(autoplayTimer);
      const paused = document.hidden || reduced.matches || !isInView || hovered || pointerHeld ||
        root.contains(document.activeElement) || contactPanel?.open || viewport.getBoundingClientRect().top < -80;
      root.dataset.autoplay = paused ? 'paused' : 'playing';
      if (paused) return;
      autoplayTimer = setTimeout(() => {
        go(current + 1, true);
      }, autoplayDelay);
    }
    function measure() {
      cancelAnimationFrame(measureFrame);
      measureFrame = requestAnimationFrame(() => {
        const height = Math.ceil(panels[current].firstElementChild.getBoundingClientRect().height);
        if (height > 0) {
          const top = viewport.getBoundingClientRect().top;
          viewport.style.height = `${height}px`;
          // A swipe near the bottom of a tall slide must not leave the shorter
          // destination almost entirely above the screen.
          if (keepChangedSlideVisible && top < 0 && top + height < Math.min(window.innerHeight / 2, 360)) {
            root.scrollIntoView({block: 'start', behavior: 'instant'});
          }
          keepChangedSlideVisible = false;
        }
      });
    }
    function activate(index, announce, updateHeight = true) {
      keepChangedSlideVisible = keepChangedSlideVisible || announce;
      current = Math.max(0, Math.min(panels.length - 1, index));
      panels.forEach((panel, i) => {
        if (i !== current && panel.contains(document.activeElement)) viewport.focus({preventScroll: true});
        panel.inert = i !== current;
        panel.setAttribute('aria-hidden', String(i !== current));
      });
      root.dataset.active = String(current);
      dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
      if (announce) status.textContent = `${status.dataset.label} ${panels[current].getAttribute('aria-label')}`;
      if (updateHeight) measure();
    }
    function settle() {
      if (mouseDrag?.dragging) return;
      clearTimeout(settleTimer);
      target = null;
      const index = Math.round(viewport.scrollLeft / Math.max(1, viewport.clientWidth));
      activate(index, index !== current);
      scheduleAutoplay();
    }
    function go(index, automatic = false) {
      index = (index + panels.length) % panels.length;
      target = index;
      activate(index, !automatic);
      viewport.scrollTo({left: viewport.clientWidth * index, behavior: reduced.matches ? 'instant' : 'smooth'});
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 800);
    }
    dots.forEach((dot, i) => dot.addEventListener('click', () => go(i), {signal}));
    viewport.addEventListener('keydown', event => {
      if (event.target !== viewport || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      go(current + (event.key === 'ArrowRight' ? 1 : -1));
    }, {signal});
    viewport.addEventListener('scroll', () => {
      if (mouseDrag?.dragging) return;
      clearTimeout(autoplayTimer);
      if (target === null) {
        const index = Math.round(viewport.scrollLeft / Math.max(1, viewport.clientWidth));
        if (index !== current) activate(index, true, false);
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 160);
    }, {passive: true, signal});
    viewport.addEventListener('scrollend', settle, {signal});
    viewport.addEventListener('pointerdown', event => {
      pointerHeld = true;
      clearTimeout(autoplayTimer);
      root.dataset.autoplay = 'paused';
      target = null;
      suppressClick = false;
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      mouseDrag = {id: event.pointerId, startX: event.clientX, startLeft: viewport.scrollLeft, index: current, dragging: false};
    }, {passive: true, signal});
    viewport.addEventListener('pointermove', event => {
      if (!mouseDrag || event.pointerId !== mouseDrag.id) return;
      const delta = event.clientX - mouseDrag.startX;
      if (!mouseDrag.dragging && Math.abs(delta) < 6) return;
      if (!mouseDrag.dragging) {
        mouseDrag.dragging = true;
        viewport.setPointerCapture(event.pointerId);
        viewport.classList.add('is-dragging');
        clearTimeout(settleTimer);
      }
      event.preventDefault();
      suppressClick = true;
      viewport.scrollLeft = mouseDrag.startLeft - delta;
    }, {signal});
    function endMouseDrag(event) {
      if (!mouseDrag || event.pointerId !== mouseDrag.id) return;
      const drag = mouseDrag;
      mouseDrag = null;
      viewport.classList.remove('is-dragging');
      if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
      if (!drag.dragging) return;
      const delta = event.clientX - drag.startX;
      const advance = event.type !== 'pointercancel' && Math.abs(delta) >= Math.min(80, viewport.clientWidth * .15);
      go(Math.max(0, Math.min(panels.length - 1, drag.index + (advance ? (delta < 0 ? 1 : -1) : 0))));
    }
    viewport.addEventListener('pointerup', endMouseDrag, {signal});
    viewport.addEventListener('pointercancel', endMouseDrag, {signal});
    viewport.addEventListener('pointerleave', event => {
      if (mouseDrag && !mouseDrag.dragging) mouseDrag = null;
    }, {signal});
    viewport.addEventListener('dragstart', event => event.preventDefault(), {signal});
    viewport.addEventListener('click', event => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, {capture: true, signal});
    root.addEventListener('pointerenter', event => {
      if (event.pointerType === 'mouse') { hovered = true; scheduleAutoplay(); }
    }, {signal});
    root.addEventListener('pointerleave', event => {
      if (event.pointerType === 'mouse') { hovered = false; scheduleAutoplay(); }
    }, {signal});
    function releasePointer() { pointerHeld = false; scheduleAutoplay(); }
    window.addEventListener('pointerup', releasePointer, {signal});
    window.addEventListener('pointercancel', releasePointer, {signal});
    root.addEventListener('focusin', scheduleAutoplay, {signal});
    root.addEventListener('focusout', () => queueMicrotask(scheduleAutoplay), {signal});
    document.addEventListener('visibilitychange', scheduleAutoplay, {signal});
    window.addEventListener('scroll', scheduleAutoplay, {passive: true, signal});
    reduced.addEventListener('change', scheduleAutoplay, {signal});
    contactPanel?.addEventListener('toggle', scheduleAutoplay, {signal});
    root.addEventListener('click', event => {
      const trigger = event.target.closest('[data-dpl-contact]');
      const contact = document.getElementById('DaceinContact');
      if (!trigger || !contact) return;
      event.preventDefault();
      event.stopPropagation();
      contact.open = true;
      const close = contact.querySelector('[data-contact-close]');
      if (close && !close.hidden) close.focus({preventScroll: true});
    }, {signal});
    const observer = new ResizeObserver(() => {
      const nextWidth = viewport.clientWidth;
      if (nextWidth !== width) {
        width = nextWidth;
        viewport.scrollTo({left: width * current, behavior: 'instant'});
      }
      measure();
    });
    panels.forEach(panel => observer.observe(panel.firstElementChild));
    const visibilityObserver = new IntersectionObserver(entries => {
      isInView = entries[0].isIntersecting;
      scheduleAutoplay();
    }, {threshold: 0});
    visibilityObserver.observe(viewport);
    hovered = window.matchMedia('(hover: hover)').matches && root.matches(':hover');
    root.dataset.ready = 'true';
    activate(0, false);
    instances.set(root, () => {
      controller.abort(); observer.disconnect(); visibilityObserver.disconnect(); clearTimeout(autoplayTimer); clearTimeout(settleTimer); cancelAnimationFrame(measureFrame); instances.delete(root);
    });
  }
  function scan(parent) {
    parent.querySelectorAll('[data-dch]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), {once: true});
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-dch]').forEach(root => instances.get(root)?.()));
})();
